import React, { useState } from "react";
import axios from "axios";
import { API_URL } from "../config";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { Box, Typography } from "@mui/material";
import { alpha, styled, keyframes, Paper, Button, Chip } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { toast } from "react-toastify";

// ─── Animations ───────────────────────────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;


// ─── Styled Components (add these alongside your existing styled components) ───

const UploadCard = styled(Paper)(({ theme }) => ({
  borderRadius: 14,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 6px 20px rgba(0,0,0,0.04)",
  padding: "20px 24px",
  marginBottom: 20,
  animation: `${fadeUp} 0.5s ease both`,
  animationDelay: "30ms",
  transition: "box-shadow 0.2s ease",
}));

const PdfDropZone = styled(Box, {
  shouldForwardProp: (p) => p !== "dragover" && p !== "hasfile",
})(({ theme, dragover, hasfile }) => ({
  border: `2px dashed ${
    hasfile
      ? theme.palette.success?.main || "#059669"
      : dragover
      ? theme.palette.primary.main
      : alpha(theme.palette.primary.main, 0.22)
  }`,
  borderRadius: 12,
  padding: "22px 16px",
  textAlign: "center",
  cursor: "pointer",
  background: hasfile
    ? alpha("#059669", 0.04)
    : dragover
    ? alpha(theme.palette.primary.main, 0.05)
    : alpha(theme.palette.background.default, 0.7),
  transition: "all 0.25s ease",
  position: "relative",
  overflow: "hidden",
  "&:hover": {
    borderColor: hasfile ? "#059669" : theme.palette.primary.main,
    background: hasfile
      ? alpha("#059669", 0.06)
      : alpha(theme.palette.primary.main, 0.04),
    "& .pdf-icon": { transform: "translateY(-3px) scale(1.08)" },
  },
}));

const UploadPrimaryBtn = styled(Button)(({ theme }) => ({
  background: "linear-gradient(135deg, #2563EB 0%, #1E40AF 100%)",
  color: "#fff",
  borderRadius: 9,
  padding: "10px 24px",
  fontSize: 13,
  fontWeight: 700,
  textTransform: "none",
  fontFamily: "'Sora', sans-serif",
  boxShadow: "0 4px 12px rgba(37,99,235,0.28)",
  transition: "all 0.2s ease",
  minWidth: 110,
  "&:hover": {
    background: "linear-gradient(135deg, #1D4ED8 0%, #1E3A8A 100%)",
    boxShadow: "0 6px 18px rgba(37,99,235,0.38)",
    transform: "translateY(-1px)",
  },
  "&:active": { transform: "translateY(0)" },
  "&.Mui-disabled": {
    background: "#E5E7EB",
    color: "#9CA3AF",
    boxShadow: "none",
  },
}));

const Spinner = styled(Box)(() => ({
  width: 20,
  height: 20,
  border: "2px solid rgba(37,99,235,0.15)",
  borderTop: "2px solid #2563EB",
  borderRadius: "50%",
  animation: `${spin} 0.7s linear infinite`,
  display: "inline-block",
}));

export default function UploadExamObjectivePDF({
  TestId,
  dragOver,
  setDragOver,
}) {
  const token = JSON.parse(localStorage.getItem("authToken"));
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const handleUpload = async () => {
    if (!selectedFile) return toast.error("Please select a PDF file first.");

    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      setLoading(true);
      const res = await axios.post(
        `${API_URL}/upload-general-objective-exam-pdf/${TestId}/`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: token ? `Bearer ${token.access}` : "",
          },
        }
      );
      toast.success(res.data.message || "File uploaded successfully.");
    } catch (err) {
      console.error(err.response?.data || err.message);
      toast.error("Error uploading file");
    } finally {
      setLoading(false);
    }
  };

  return (
    <UploadCard>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "9px",
            background: alpha("#2563EB", 0.1),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <PictureAsPdfIcon sx={{ fontSize: 17, color: "primary.main" }} />
        </Box>
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 700,
            color: "text.primary",
            fontFamily: "'Sora', sans-serif",
            letterSpacing: "0.1px",
          }}
        >
          Upload Exam PDF
        </Typography>
        {selectedFile && (
          <Chip
            label="File ready"
            size="small"
            sx={{
              ml: "auto",
              fontFamily: "'Sora', sans-serif",
              fontWeight: 700,
              fontSize: 10,
              letterSpacing: "0.4px",
              textTransform: "uppercase",
              background: alpha("#059669", 0.1),
              color: "#059669",
              border: `1px solid ${alpha("#059669", 0.22)}`,
            }}
          />
        )}
      </Box>

      {/* Drop zone */}
      <PdfDropZone
        dragover={dragOver ? 1 : 0}
        hasfile={selectedFile ? 1 : 0}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          const file = e.dataTransfer.files[0];
          if (file && file.type === "application/pdf")
            handleFileChange({ target: { files: [file] } });
        }}
        onClick={() => document.getElementById("pdf-upload-input")?.click()}
      >
        <input
          id="pdf-upload-input"
          type="file"
          accept="application/pdf"
          style={{ display: "none" }}
          onChange={handleFileChange}
        />

        {selectedFile ? (
          // File selected state
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 0.6,
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 3,
                background: alpha("#059669", 0.1),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 0.5,
              }}
              className="pdf-icon"
            >
              <InsertDriveFileIcon sx={{ fontSize: 24, color: "#059669" }} />
            </Box>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 700,
                color: "#059669",
                fontFamily: "'Sora', sans-serif",
              }}
            >
              {selectedFile.name}
            </Typography>
            <Typography
              sx={{
                fontSize: 11,
                color: "text.disabled",
                fontFamily: "'Sora', sans-serif",
              }}
            >
              {(selectedFile.size / 1024 / 1024).toFixed(2)} MB · PDF
            </Typography>
            <Typography
              sx={{
                fontSize: 11,
                color: "text.disabled",
                fontFamily: "'Sora', sans-serif",
                mt: 0.5,
              }}
            >
              Click to change file
            </Typography>
          </Box>
        ) : (
          // Empty state
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <Box
              className="pdf-icon"
              sx={{
                width: 44,
                height: 44,
                borderRadius: 3,
                background: alpha("#2563EB", 0.08),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 0.5,
                transition: "transform 0.25s ease",
              }}
            >
              <CloudUploadIcon
                sx={{ fontSize: 24, color: "primary.main", opacity: 0.75 }}
              />
            </Box>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: "text.primary",
                fontFamily: "'Sora', sans-serif",
              }}
            >
              Drop your PDF here
            </Typography>
            <Typography
              sx={{
                fontSize: 11,
                color: "text.disabled",
                fontFamily: "'Sora', sans-serif",
              }}
            >
              or click to browse · PDF only · max 20 MB
            </Typography>
          </Box>
        )}
      </PdfDropZone>

      {/* Upload button row */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          mt: 2,
          gap: 1.5,
        }}
      >
        {selectedFile && !loading && (
          <Button
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleFileChange({ target: { files: [] } }); // clear file — wire to your state setter
            }}
            sx={{
              fontSize: 12,
              fontWeight: 600,
              textTransform: "none",
              color: "text.disabled",
              fontFamily: "'Sora', sans-serif",
              borderRadius: 2,
              px: 1.5,
              "&:hover": {
                color: "error.main",
                background: alpha("#DC2626", 0.05),
              },
            }}
          >
            Clear
          </Button>
        )}

        <UploadPrimaryBtn
          disabled={loading || !selectedFile}
          onClick={handleUpload}
          startIcon={
            loading ? (
              <Spinner sx={{ width: 14, height: 14 }} />
            ) : (
              <CloudUploadIcon sx={{ fontSize: "16px !important" }} />
            )
          }
        >
          {loading ? "Uploading…" : "Upload"}
        </UploadPrimaryBtn>
      </Box>

      {/* Progress bar while uploading */}
      {loading && (
        <Box
          sx={{
            mt: 2,
            borderRadius: 2,
            overflow: "hidden",
            height: 4,
            background: alpha("#2563EB", 0.1),
          }}
        >
          <Box
            sx={{
              height: "100%",
              borderRadius: 2,
              background: "linear-gradient(90deg, #2563EB, #60A5FA, #2563EB)",
              backgroundSize: "200% 100%",
              animation: "shimmer 1.4s infinite linear",
              "@keyframes shimmer": {
                "0%": { backgroundPosition: "200% 0" },
                "100%": { backgroundPosition: "-200% 0" },
              },
            }}
          />
        </Box>
      )}
    </UploadCard>
  );
}
